import React, { useMemo } from "react";

import { SOP, SOP_TABS } from "../../../constants";

import PrimaryButton from "../../../stories/Components/Buttons/PrimaryButton";
import FilesTable from "../../../stories/Components/FilesTable/FilesTable";
import InlineInput from "../../../stories/Components/Input/InlineInput";
import ImagesAndVideosWidget from "../../../stories/Components/MediaWidget/ImagesAndVideosWidget";
import SiteHeader from "../../../stories/Components/SiteHeader/SiteHeader";
import WidgetContainer from "../../../stories/Components/Widget/WidgetContainer";
import BackButton from "../../../stories/Components/WidgetEditorView/Header/BackButton";
import TableActionsIconsGroup from "../../../stories/Components/Table/TableActionsIconsGroup";
import SopHistory from "../../../components/Sop/HistoryList";
import SopActivity from "../../../components/Sop/Activity";
import SopDeleteModal from "../../../components/Sop/DeleteModal";
import SopDetailsView from "../../../components/Sop/DetailsView";

import { hasWritePermission } from "../../../helpers/Permissions";
import useSopDetailsData from "./useDetailsData";

const SopDetails = () => {
  const {
    addNewStep,
    associatedFiles,
    catOptions,
    disableEditing,
    disableUploadMedia,
    editing,
    handleAddMedia,
    handleFileClone,
    handleNavigationBack,
    handleRemoveMedia,
    handleSetPrimaryMedia,
    handleUpdateFile,
    loading,
    onAddFilesCallback,
    removeFilesAndUpdateApi,
    setShowActivity,
    showActivity,
    sop,
    sopDispatch,
    sopId,
    sopState,
    stepFocus,
    updateSopFiles,
    setStepFocus,
    handleChangeName,
    buttonActions,
    handleEditClick,
    onFinishEditing,
    activeTab,
    setActiveTab,
    resetSopState,
    showEditIcon,
    someSopInUse,
    canDeleteSop,
    setCanDeleteSop,
    setAddNewStep,
    currentUser,
  } = useSopDetailsData();

  const tabs = useMemo(
    () => [
      {
        id: SOP_TABS.DETAILS_ID,
        title: SOP_TABS.DETAILS_TITLE,
        content: (
          <SopDetailsView
            loading={loading}
            sop={sopState}
            sopDispatch={sopDispatch}
            isEditing={editing}
            stepData={sopState?.steps}
            categoryOptionsList={catOptions}
            addNewStep={addNewStep}
            setAddNewStep={setAddNewStep}
            stepFocus={stepFocus}
            setStepFocus={setStepFocus}
          />
        ),
      },
      {
        id: SOP_TABS.MEDIA_ID,
        title: SOP_TABS.MEDIA_TITLE,
        content: (
          <ImagesAndVideosWidget
            border={false}
            resource={sop}
            disableEditing={disableUploadMedia}
            hasWritePermission={hasWritePermission(SOP, currentUser)}
            hasDeletePermission={hasWritePermission(SOP, currentUser)}
            handleAddMedia={handleAddMedia}
            handleSetPrimaryMedia={handleSetPrimaryMedia}
            handleRemoveMedia={handleRemoveMedia}
          />
        ),
      },
      {
        id: SOP_TABS.FILES_ID,
        title: SOP_TABS.FILES_TITLE,
        content: (
          <FilesTable
            onAddFilesCallback={onAddFilesCallback}
            removeFilesAndUpdateApi={removeFilesAndUpdateApi}
            onRemoveFilesCallback={updateSopFiles}
            files={associatedFiles}
            resourceName="Sop"
            association={`Sop/${sopId}`}
            hasEditPermission
            hasDeletePermission={currentUser?.hasPermission?.(
              "sop",
              "can_delete"
            )}
            hasWritePermission={currentUser?.hasPermission?.(
              "sop",
              "can_write"
            )}
            handleFileClone={handleFileClone}
            handleUpdateFile={handleUpdateFile}
          />
        ),
      },
      {
        id: SOP_TABS.HISTORY_ID,
        title: SOP_TABS.HISTORY_TITLE,
        content: <SopHistory sop={sop} />,
      },
    ],
    [
      addNewStep,
      associatedFiles,
      catOptions,
      currentUser,
      disableUploadMedia,
      editing,
      handleAddMedia,
      handleFileClone,
      handleRemoveMedia,
      handleSetPrimaryMedia,
      handleUpdateFile,
      loading,
      onAddFilesCallback,
      removeFilesAndUpdateApi,
      setAddNewStep,
      setStepFocus,
      sop,
      sopDispatch,
      sopId,
      sopState,
      stepFocus,
      updateSopFiles,
    ]
  );

  return (
    <>
      <SiteHeader
        title={
          <div className="flex items-center">
            <InlineInput
              width="w-full"
              size="custom4xl"
              value={sop?.name}
              editing={editing}
              loading={!sop?.name}
              disabled={!sop?.name}
              fontWeight="bold"
              color="gray-650"
              onConfirm={handleChangeName}
              onChangeCallback={handleChangeName}
              hidePencil
              isHeaderTitle
            />
          </div>
        }
        buttons={
          !!buttonActions?.length && (
            <PrimaryButton
              title="Actions"
              dropdownItems={buttonActions}
              large
            />
          )
        }
        hideDropdownContainer
      />

      <BackButton onBackPressed={handleNavigationBack} />

      {showActivity ? (
        <SopActivity sopId={sopId} />
      ) : (
        <WidgetContainer
          className="p-4 border-gray-200 shadow-lg border rounded-md"
          style={{ minWidth: "903px" }}
          isEditing={editing}
          handleEditClick={handleEditClick}
          onFinishEditing={onFinishEditing}
          tabs={tabs}
          loading={loading}
          disableEditing={disableEditing}
          activeTab={activeTab}
          onTabClick={(t) => setActiveTab(t)}
          resetResourceState={resetSopState}
          actionsButtons={
            <TableActionsIconsGroup
              style={{ height: "100%" }}
              canEdit={!disableEditing && showEditIcon}
              showActivityIcon
              handleEdit={handleEditClick}
              onActivityClicked={() => setShowActivity(true)}
            />
          }
        />
      )}

      <SopDeleteModal
        someSopInUse={someSopInUse}
        sopArray={[sopState]}
        canDeleteSop={canDeleteSop}
        setCanDeleteSop={setCanDeleteSop}
      />
    </>
  );
};

export default SopDetails;
